import BlockIcon from "@mui/icons-material/Block";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import InfoIcon from "@mui/icons-material/Info";
import WarningIcon from "@mui/icons-material/Warning";
import {
  Alert,
  AlertTitle,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Typography,
  type SxProps,
  type Theme,
} from "@mui/material";
import { memo, useMemo, type ReactElement } from "react";
import {
  TenediReadinessStatus,
  type TenediConnectionQuery,
} from "../../../generated/graphql";
import { READINESS_SECTION_LABELS } from "../tenedi-connection-copy";

export type ReadinessItem =
  TenediConnectionQuery["tenediConnection"]["readiness"][number];

const styles = {
  paper: { p: 2 } satisfies SxProps<Theme>,
  icon: { minWidth: 36 } satisfies SxProps<Theme>,
};

/** Blocking first, then warnings, then the rest, keeping the server's order inside each. */
const STATUS_ORDER: Record<TenediReadinessStatus, number> = {
  [TenediReadinessStatus.Blocking]: 0,
  [TenediReadinessStatus.Warning]: 1,
  [TenediReadinessStatus.Info]: 2,
  [TenediReadinessStatus.Ready]: 3,
};

const STATUS_ICONS: Record<TenediReadinessStatus, ReactElement> = {
  [TenediReadinessStatus.Blocking]: <BlockIcon color="error" />,
  [TenediReadinessStatus.Warning]: <WarningIcon color="warning" />,
  [TenediReadinessStatus.Info]: <InfoIcon color="info" />,
  [TenediReadinessStatus.Ready]: <CheckCircleIcon color="success" />,
};

const STATUS_LABELS: Record<TenediReadinessStatus, string> = {
  [TenediReadinessStatus.Blocking]: "Blocks",
  [TenediReadinessStatus.Warning]: "Check",
  [TenediReadinessStatus.Info]: "Reminder",
  [TenediReadinessStatus.Ready]: "Ready",
};

/** The checklist sorted for reading: what blocks comes first. */
export const sortReadiness = (
  items: readonly ReadinessItem[],
): ReadinessItem[] =>
  items
    .map((item, index) => ({ item, index }))
    .sort((left, right) => {
      const byStatus =
        STATUS_ORDER[left.item.status] - STATUS_ORDER[right.item.status];
      return byStatus === 0 ? left.index - right.index : byStatus;
    })
    .map(({ item }) => item);

/** The panel's headline for a checklist. */
export const readinessHeadline = (items: readonly ReadinessItem[]): string => {
  const blocking = items.filter(
    (item) => item.status === TenediReadinessStatus.Blocking,
  ).length;
  if (blocking === 0) {
    return "Ready to test. Open the bench from a partnership below.";
  }
  return blocking === 1
    ? "1 item stops messages from flowing."
    : `${blocking} items stop messages from flowing.`;
};

type ReadinessRowProps = { readonly item: ReadinessItem };

const ReadinessRow = memo(({ item }: ReadinessRowProps) => (
  <ListItem disableGutters>
    <ListItemIcon sx={styles.icon}>{STATUS_ICONS[item.status]}</ListItemIcon>
    <ListItemText
      primary={item.message}
      secondary={`${STATUS_LABELS[item.status]} · Fix in: ${
        READINESS_SECTION_LABELS[item.fixIn]
      }`}
    />
  </ListItem>
));
ReadinessRow.displayName = "ReadinessRow";

type ReadinessPanelProps = {
  readonly items: readonly ReadinessItem[];
  readonly ready: boolean;
};

/**
 * The server-computed readiness checklist (plan section 2.5). The page only
 * renders it; the backend decides each item.
 */
const ReadinessPanel = ({ items, ready }: ReadinessPanelProps) => {
  const sorted = useMemo(() => sortReadiness(items), [items]);
  const headline = useMemo(() => readinessHeadline(items), [items]);

  return (
    <Paper sx={styles.paper} variant="outlined">
      <Typography gutterBottom variant="h6">
        Readiness
      </Typography>
      <Alert severity={ready ? "success" : "error"}>
        <AlertTitle>{headline}</AlertTitle>
      </Alert>
      <List dense>
        {sorted.map((item) => (
          <ReadinessRow key={item.check} item={item} />
        ))}
      </List>
    </Paper>
  );
};

export default ReadinessPanel;
